import { useMutation, useQuery } from '@tanstack/react-query';
import { updateCompanyUserNotificationSetting } from '~apis/companyuser/updateCompanyUserNotificationSetting';
import {
  cancelInvitation,
  createInviteLink,
  getInviteLink,
  invite,
  inviteMemberBulk,
  updateInviteRole,
} from '~apis/member';
import {
  getCompanies as getUserCompanies,
  getUserFeatureFlag,
  getReferralSummary,
  getReferrals,
  getUser,
  getUserExperiments,
} from '~apis/user';
import { queryClient } from '~libs/queryClient';

import { CompaniesQueryKey } from '../companies/query';
import { SettingsQueryKey } from '../setting/query';
import { USE_GET_COMPANY_INVITED_USERS_QUERY } from '../useInvitedMembersQuery';

export const UserCompaniesQueryKey = {
  default: () => ['user_companies'],
  featureFlag: (companyId: string) => ['user_feature_flag', companyId],
};

export function useUserCompanies({ userId }: { userId: string }) {
  return useQuery({
    queryKey: UserCompaniesQueryKey.default(),
    queryFn: () => getUserCompanies({ userId }),
    enabled: !!userId,
  });
}

export default function useUpdateCompanyUserNotificationSetting() {
  return useMutation({
    mutationFn: updateCompanyUserNotificationSetting,
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({
        queryKey: CompaniesQueryKey.members({ companyId }),
      });
    },
  });
}

export function useUserFeatureFlag({ companyId }: { companyId: string }) {
  return useQuery({
    queryKey: UserCompaniesQueryKey.featureFlag(companyId),
    queryFn: () => getUserFeatureFlag({ companyId }),
    enabled: !!companyId,
  });
}

export function useReferralSummary(userId: string) {
  return useQuery({
    queryKey: ['referralSummary', userId],
    queryFn: () => getReferralSummary(userId),
    enabled: !!userId,
  });
}

export function useReferrals(userId: string) {
  return useQuery({
    queryKey: ['referrals', userId],
    queryFn: () => getReferrals(userId),
    enabled: !!userId,
  });
}

export function useInviteMember(companyId: string) {
  return useMutation({
    mutationFn: (email: string) => invite(companyId, { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...SettingsQueryKey.onboardingStatus({ companyId })],
      });
      queryClient.invalidateQueries({
        queryKey: USE_GET_COMPANY_INVITED_USERS_QUERY.default(companyId as string),
      });
    },
  });
}

export function useInviteMemberBulk(companyId: string) {
  return useMutation({
    mutationFn: (invitations: Array<{ email: string; roleId: number }>) =>
      inviteMemberBulk(companyId, { invitations }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...SettingsQueryKey.onboardingStatus({ companyId })],
      });
      queryClient.invalidateQueries({
        queryKey: USE_GET_COMPANY_INVITED_USERS_QUERY.default(companyId as string),
      });
    },
  });
}
export function useGetInviteLink(companyId: string) {
  return useQuery({
    queryKey: ['inviteLink', companyId],
    queryFn: () => getInviteLink(companyId),
    enabled: !!companyId,
  });
}

export function useCreateInviteLink(companyId: string) {
  return useMutation({
    mutationFn: (roleId: number) => createInviteLink(companyId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['inviteLink', companyId],
      });
    },
  });
}

export function useCancelInvitation(companyId: string) {
  return useMutation({
    mutationFn: (invitationId: number) => cancelInvitation(companyId, invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['inviteLink', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: USE_GET_COMPANY_INVITED_USERS_QUERY.default(companyId as string),
      });
    },
  });
}

export function useUpdateInviteRole(companyId: string) {
  return useMutation({
    mutationFn: ({ invitationId, roleId }: { invitationId: number; roleId: number }) =>
      updateInviteRole(companyId, invitationId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['inviteLink', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: USE_GET_COMPANY_INVITED_USERS_QUERY.default(companyId as string),
      });
    },
  });
}

export function useGetUser(userId?: string) {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId ?? ''),
    enabled: !!userId,
  });
}

export const UserExperimentsQueryKey = {
  default: (userId: string) => ['user_experiments', userId],
};

export function useUserExperiments({ userId }: { userId: string }) {
  return useQuery({
    queryKey: UserExperimentsQueryKey.default(userId),
    queryFn: () => getUserExperiments(userId),
    enabled: !!userId,
  });
}
